// Markdown.tsx — Shared markdown renderer.
// Wraps react-markdown + remark-gfm with a small set of tag overrides
// that keep prose readable inside dark, centered UIs. Used by apps
// that accept user-authored card/question text (e.g. imprinter).
//
// Security note: react-markdown does NOT render raw HTML by default,
// and we don't enable the rehype-raw plugin — so embedded <script>
// tags or HTML injection from card content is inert. The optional
// `math` path does not change that: rehype-katex adds no raw-HTML
// pass, and KaTeX's `trust` option stays at its default of false, so
// \href and \includegraphics cannot smuggle markup back in.

import React from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';

// KaTeX is ~300KB of JS plus a stylesheet and fonts — an order of
// magnitude more than the rest of this module. It loads on first use of
// `math` and is cached module-wide, so an SPA that never renders a formula
// never pays for it, and one that renders many pays once.
type MathPlugins = { remarkMath: unknown; rehypeKatex: unknown };
let mathPlugins: MathPlugins | null = null;
let mathLoad: Promise<MathPlugins> | null = null;

// KaTeX options for card/question content. `throwOnError: false` prints a bad
// expression in red instead of taking the question down mid-session, `trust`
// stays false so \href and \includegraphics cannot smuggle markup back in, and
// `strict: false` accepts what an author actually types — a bare `°` or a
// unicode minus inside a formula — rather than warning on every render.
const KATEX_OPTIONS = {
    throwOnError: false,
    trust: false,
    strict: false as const,
    errorColor: 'var(--color-text-danger, #dc3545)',
};

function loadMath(): Promise<MathPlugins> {
    if (!mathLoad) {
        mathLoad = Promise.all([
            import('remark-math'),
            import('rehype-katex'),
            // Side-effect only; typed by katex-css.d.ts in this directory.
            import('katex/dist/katex.min.css'),
        ]).then(([rm, rk]) => {
            mathPlugins = { remarkMath: rm.default, rehypeKatex: rk.default };
            return mathPlugins;
        });
    }
    return mathLoad;
}

export interface MarkdownProps {
    children: string;
    // className applied to the wrapping <div>, e.g. for sizing / spacing.
    className?: string;
    // Opt in to LaTeX: `$inline$` and `$$display$$` render via KaTeX.
    // Off by default because KaTeX is the heaviest thing this component
    // can pull in — see loadMath above. While the chunk is in flight the
    // formula renders as its source text, then swaps in; that beats
    // blocking the whole message on a stylesheet.
    //
    // Note the cost of turning it on: `$` becomes a delimiter, so a string
    // that happens to carry two of them ("$360 ÷ 4 = $90") renders the span
    // between as math. Authors escape a literal one as `\$`.
    math?: boolean;
    // Extra rehype plugins, appended after the built-in pipeline. This is the
    // one seam a consumer has into the rendered tree: imprinter uses it to make
    // every number in a calculator question clickable, which cannot be done in
    // the Markdown source without also rewriting what the author typed.
    //
    // A plugin here sees hast, not raw HTML, so it cannot reintroduce the
    // markup the security note above rules out — but it CAN synthesize
    // elements, so anything passed must be trusted code, never content.
    rehypePlugins?: unknown[];
    // Opt in to inline `data:image/...` URIs so content can embed images
    // directly (e.g. imprinter cards carrying a cropped VFR chart section).
    // Off by default: react-markdown's defaultUrlTransform strips `data:`
    // URIs, which we preserve for every other consumer. Only the image
    // subset is allowed through; `data:text/html`, `javascript:`, etc. are
    // still sanitized via defaultUrlTransform.
    allowDataImages?: boolean;
}

export const Markdown: React.FC<MarkdownProps> = ({ children, className, allowDataImages, math, rehypePlugins }) => {
    // Re-render once the KaTeX chunk lands. Starting from the module cache
    // means a second <Markdown math> mounts with plugins already in hand
    // and never flashes raw LaTeX.
    const [mathReady, setMathReady] = React.useState(() => mathPlugins !== null);
    React.useEffect(() => {
        if (!math || mathPlugins !== null) return;
        let live = true;
        void loadMath().then(() => { if (live) setMathReady(true); });
        return () => { live = false; };
    }, [math]);
    const useMath = !!math && mathPlugins !== null && mathReady;
    // Allow only raster image data URIs (png/jpeg/gif/webp/avif). SVG is
    // excluded on purpose — it can carry markup — and every non-image `data:`
    // (e.g. data:text/html) still falls through to defaultUrlTransform, which
    // strips it. Everything else keeps react-markdown's default sanitizing.
    const urlTransform = allowDataImages
        ? (url: string) =>
              /^data:image\/(png|jpe?g|gif|webp|avif)[;,]/i.test(url) ? url : defaultUrlTransform(url)
        : undefined;
    return (
        <div className={className}>
            <ReactMarkdown
                remarkPlugins={useMath ? [remarkGfm, mathPlugins!.remarkMath as never] : [remarkGfm]}
                rehypePlugins={[
                    ...(useMath ? [[mathPlugins!.rehypeKatex, KATEX_OPTIONS] as never] : []),
                    // Last, so a consumer's plugin sees the finished tree —
                    // including whatever KaTeX has just built. A plugin that
                    // must not walk into that skips it by class, the way
                    // anything walking a rendered tree has to.
                    ...((rehypePlugins ?? []) as never[]),
                ]}
                urlTransform={urlTransform}
                components={{
                    img: ({ src, alt }) => (
                        <img
                            src={typeof src === 'string' ? src : undefined}
                            alt={alt ?? ''}
                            style={{
                                maxWidth: '100%',
                                height: 'auto',
                                display: 'block',
                                margin: '0.5em auto',
                                borderRadius: 'var(--radius-sm)',
                                border: '1px solid var(--color-border-subtle)',
                            }}
                        />
                    ),
                    // Collapse paragraph margin for single-paragraph card fronts.
                    p: ({ children: c }) => <p style={{ margin: '0 0 0.75em 0' }}>{c}</p>,
                    code: ({ children: c, className: cn }) => {
                        if (cn) {
                            return (
                                <pre style={{
                                    background: 'var(--color-surface-overlay)',
                                    border: '1px solid var(--color-border-subtle)',
                                    borderRadius: 'var(--radius-sm)',
                                    padding: '0.75rem 1rem',
                                    overflowX: 'auto',
                                    fontFamily: 'var(--type-code-family)',
                                    fontSize: 'var(--type-code-size)',
                                    lineHeight: 'var(--type-code-line-height)',
                                    color: 'var(--color-text-primary)',
                                    textAlign: 'left',
                                }}><code className={cn}>{c}</code></pre>
                            );
                        }
                        return (
                            <code style={{
                                background: 'var(--color-surface-overlay-strong)',
                                padding: '0.1em 0.35em',
                                borderRadius: 'var(--radius-sm)',
                                fontFamily: 'var(--type-code-family)',
                                fontSize: 'var(--type-code-size)',
                                color: 'var(--color-text-primary)',
                            }}>{c}</code>
                        );
                    },
                    ul: ({ children: c }) => <ul style={{ textAlign: 'left', margin: '0.5em 0', paddingLeft: '1.5em' }}>{c}</ul>,
                    ol: ({ children: c }) => <ol style={{ textAlign: 'left', margin: '0.5em 0', paddingLeft: '1.5em' }}>{c}</ol>,
                    li: ({ children: c }) => <li style={{ marginBottom: '0.25em' }}>{c}</li>,
                    a: ({ children: c, href }) => (
                        <a href={href} target="_blank" rel="noopener noreferrer"
                           style={{ color: 'var(--color-link)' }}>{c}</a>
                    ),
                    blockquote: ({ children: c }) => (
                        <blockquote style={{
                            borderLeft: '3px solid var(--color-border-default)',
                            paddingLeft: '1rem',
                            margin: '0.5em 0',
                            color: 'var(--color-text-secondary)',
                            textAlign: 'left',
                        }}>{c}</blockquote>
                    ),
                    table: ({ children: c }) => (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ borderCollapse: 'collapse', margin: '0.5em 0', textAlign: 'left' }}>{c}</table>
                        </div>
                    ),
                    // A GFM column alignment (`---:`) reaches a cell as a
                    // `style` prop carrying textAlign — react-markdown converts
                    // the hast `align` property for us. Spread it last, or the
                    // override drops it and a column of numbers renders
                    // ragged-left, digits no longer under the digits above.
                    th: ({ children: c, style: s }) => (
                        <th style={{ borderBottom: '2px solid var(--color-border-default)', padding: '0.4em 0.8em', ...s }}>{c}</th>
                    ),
                    td: ({ children: c, style: s }) => (
                        <td style={{ borderBottom: '1px solid var(--color-border-subtle)', padding: '0.4em 0.8em', ...s }}>{c}</td>
                    ),
                }}
            >
                {children}
            </ReactMarkdown>
        </div>
    );
};

export default Markdown;
